import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogPostPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${id}`);
        setPost(response.data.post);
        
        // Charger les articles liés
        if (response.data.post.category) {
          const allPostsRes = await axios.get(`${API}/blog`);
          const related = allPostsRes.data.posts
            .filter(p => p.category === response.data.post.category && p.id !== id && p.isPublished)
            .slice(0, 3);
          setRelatedPosts(related);
        }
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#C9A96E] animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FDF8F3]">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="text-4xl font-serif font-bold text-[#2C1810] mb-4">Article non trouvé</h1>
          <Link to="/blog" className="text-[#C9A96E] hover:underline">← Retour au blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Back button */}
      <div className="bg-white border-b border-[#E8DDD4]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-[#C9A96E] hover:text-[#A67B5B] transition-colors"
          >
            <ArrowLeft size={20} />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            {post.category && (
              <span className="inline-block px-4 py-1.5 bg-[#C9A96E] text-white text-sm font-medium rounded-full mb-4">
                {post.category}
              </span>
            )}
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#2C1810] mb-4">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-[#A67B5B]">
              {post.author && (
                <div className="flex items-center gap-2">
                  <User size={16} />
                  <span>{post.author}</span>
                </div>
              )}
              {post.publishedAt && (
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span>{format(new Date(post.publishedAt), 'dd MMMM yyyy', { locale: fr })}</span>
                </div>
              )}
            </div>
          </div>

          {/* Featured Image */}
          {post.image && (
            <div className="aspect-video bg-[#F5F0EB] rounded-2xl overflow-hidden mb-8">
              <img
                src={post.image.startsWith('/uploads') ? `${BACKEND_URL}${post.image}` : post.image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Excerpt */}
          {post.excerpt && (
            <div className="text-xl text-[#5D4037] leading-relaxed mb-8 p-6 bg-[#F5F0EB] rounded-xl border-l-4 border-[#C9A96E]">
              {post.excerpt}
            </div>
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div 
              className="text-[#5D4037] leading-relaxed whitespace-pre-wrap"
              style={{ 
                fontSize: '1.125rem', 
                lineHeight: '1.8',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >
              {post.content}
            </div>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-[#E8DDD4]">
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1 bg-[#F5F0EB] text-[#A67B5B] text-sm rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-serif font-bold text-[#2C1810] mb-8">Articles similaires</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.id} 
                  to={`/blog/${relatedPost.slug || relatedPost.id}`}
                  className="group"
                >
                  <div className="bg-[#FDF8F3] rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all">
                    <div className="aspect-video bg-[#F5F0EB] relative overflow-hidden">
                      {relatedPost.image ? (
                        <img
                          src={relatedPost.image.startsWith('/uploads') ? `${BACKEND_URL}${relatedPost.image}` : relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Calendar size={48} className="text-[#D4C4B5]" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-[#2C1810] line-clamp-2 group-hover:text-[#C9A96E] transition-colors">
                        {relatedPost.title}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
